import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useRoutes } from "react-router-dom";
import { UserRound } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { bindQueryResume } from "@/boot/query-sync";

import { ensureAccessToken, tryRefresh } from "@/boot/auth";
import { isInTelegram } from "@/boot/telegram";
import { BottomNav, SideNav } from "@/components/BottomNav";
import { CaptureSheet } from "@/components/CaptureBar";
import { ProductLogo } from "@/components/Logo";
import { appRoutes } from "@/routes";
import { Spinner } from "@/components/ui/Spinner";
import { SplashLoginView } from "@/views/SplashLoginView";
import { SplashView } from "@/views/SplashView";

type BootState = "loading" | "authed" | "web_anonymous" | "error";

function AppRoutes({ onSignOut }: { onSignOut: () => void }) {
  return useRoutes(appRoutes(onSignOut));
}

/**
 * A task is a conversation (Spec 021): on a phone its screen has no bottom bar,
 * so the message field sits at the bottom, as on the canvas. Telegram's Back
 * returns to the list. The shared chat, an idea and a case (Spec 025) are the same.
 */
function isTaskScreen(pathname: string): boolean {
  return (/^\/tasks\/[^/]+$/.test(pathname) && pathname !== "/tasks/stale") || pathname === "/chat/general"
    || /^\/knowledge\/sources\/idea\/[^/]+$/.test(pathname) || /^\/knowledge\/cases\/[^/]+$/.test(pathname);
}

/**
 * Spec 022: on a computer these are a list with what is open next to it, so
 * they take the whole width; every other screen keeps a readable column.
 * «Отделы» (Spec 035) is such a list: in the 760px column the unit beside it got 328px.
 */
function isPaneScreen(pathname: string): boolean {
  return pathname === "/" || pathname === "/chat" || /^\/projects\/[^/]+$/.test(pathname) || pathname === "/knowledge/cases"
    || pathname === "/agents" || pathname === "/departments";
}

/**
 * The column on a computer. A task has room for its status column (PC audit 28.09, point 17);
 * the overviews of cards — «Проекты», «Знания», «Статистика» — lay their grids out wider
 * (UX audit 15.10, L-2); a page of one thing — a card, a form, a text — keeps 760px to read.
 */
function columnOf(pathname: string): string {
  if (isPaneScreen(pathname)) return "pt-3 lg:pt-0";
  if (/^\/tasks\/[^/]+$/.test(pathname) || pathname === "/projects" || pathname === "/knowledge" || pathname === "/knowledge/stats") {
    return "pt-3 lg:max-w-[1180px] lg:pt-0";
  }
  return "pt-3 lg:max-w-[760px] lg:pt-0";
}

export default function App() {
  const [state, setState] = useState<BootState>("loading");
  const { pathname } = useLocation();
  const [capturing, setCapturing] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (state === "authed") return bindQueryResume(queryClient);
  }, [state, queryClient]);

  const boot = useCallback(async () => {
    setState("loading");
    if (isInTelegram()) {
      const token = await ensureAccessToken();
      setState(token ? "authed" : "error");
      return;
    }
    if (await tryRefresh()) {
      setState("authed");
      return;
    }
    setState("web_anonymous");
  }, []);

  useEffect(() => {
    void boot();
  }, [boot]);

  const handleSignedOut = useCallback(() => {
    setState("web_anonymous");
  }, []);

  if (state === "loading") return <Spinner label="Подключаемся…" />;
  if (state === "error") return <SplashView />;
  if (state === "web_anonymous") return <SplashLoginView onAuthed={() => void boot()} />;

  const taskScreen = isTaskScreen(pathname);
  const openCapture = () => setCapturing(true);

  return (
    // Phones: one 480 px column with the bottom bar. Computers (≥1024 px,
    // Spec 021): the sidebar on the left and a wide content column.
    <div className="mx-auto flex min-h-screen w-full max-w-app flex-col lg:max-w-[1440px] lg:flex-row lg:gap-6 lg:px-6 xl:gap-10 xl:px-8">
      <SideNav onCapture={openCapture} logo={
          <Link to="/" aria-label="Просто Таск — сейчас">
            <ProductLogo size={32} markBelowXl />
          </Link>
        } profile={
          <Link to="/me" aria-label="Профиль" title="Профиль" className="press-surface flex items-center gap-3 rounded-full pr-3 text-cell text-p-text-muted hover:text-p-text">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-p-border bg-p-surface text-p-text">
              <UserRound className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="hidden xl:inline">Профиль</span>
          </Link>
        } />
      {/*
        §2 — 56px reserved for Telegram's own header, 96px at the bottom so the
        last card clears the 88px tab bar. Horizontal field padding is 16.
      */}
      <main className={`min-w-0 flex-1 px-4 pt-safe-top lg:px-0 lg:pb-12 lg:pt-8 ${taskScreen ? "pb-4" : "pb-[calc(96px+env(safe-area-inset-bottom))] lg:pb-12"}`}>
        {/* Spec 022: no logo bar on a phone — Telegram has its own header;
            the profile sits next to the title of «Решить». */}
        <div className={columnOf(pathname)}>
          <AppRoutes onSignOut={handleSignedOut} />
        </div>
      </main>
      {!taskScreen && <BottomNav onCapture={openCapture} />}
      {/* ●Записать — the quick capture as a sheet on every screen (D5). */}
      <CaptureSheet
        open={capturing}
        onClose={() => setCapturing(false)}
        onCaptured={() => void queryClient.invalidateQueries({ queryKey: ["overview"] })}
      />
    </div>
  );
}
